import {createAsyncThunk} from '@reduxjs/toolkit';
import type {
  FetchCampaignRecipientInsightsArg,
  FetchCampaignRecipientInsightsResponse,
  FetchCampaignRecipientInsightsTotalRowsArg,
  FetchCampaignRecipientInsightsTotalRowsResponse,
  FetchNegativeOutcomeCountsArg,
  FetchRemovableCampaignEmailsArg,
  FetchRemovableMailingListEmailsArg,
  FetchMailingListRecipientInsightsArg,
  FetchMailingListRecipientInsightsResponse,
  FetchMailingListRecipientInsightsTotalRowsArg,
  FetchMailingListRecipientInsightsTotalRowsResponse,
  InitEmailMarketingCampaignPerformanceArg,
  InitEmailMarketingCampaignPerformanceResponse,
  NegativeOutcomeRecipientCounts,
  RemovableEmailsPage,
  RemoveAllEmailsArg,
  RemoveEmailsArg,
} from './email-marketing-campaign-performance.types';
import {REMOVABLE_EMAILS_FETCH_LIMIT} from './email-marketing-campaign-performance.types';

const REMOVE_ALL_EMAILS_FLAG = 1;

export const initEmailMarketingCampaignPerformance = createAsyncThunk(
  'initEmailMarketingCampaignPerformance',
  async (data: InitEmailMarketingCampaignPerformanceArg): Promise<InitEmailMarketingCampaignPerformanceResponse> => {
    return (await window.PMW.readLocal('emailmarketing/initEmailMarketingCampaignPerformance', data)) as InitEmailMarketingCampaignPerformanceResponse;
  }
);

export const fetchCampaignRecipientInsights = createAsyncThunk(
  'fetchCampaignRecipientInsights',
  async (data: FetchCampaignRecipientInsightsArg): Promise<FetchCampaignRecipientInsightsResponse> => {
    return (await window.PMW.readLocal('emailmarketing/getCampaignRecipientInsights', data)) as FetchCampaignRecipientInsightsResponse;
  }
);

export const fetchRemovableCampaignEmails = createAsyncThunk(
  'fetchRemovableCampaignEmails',
  async (data: FetchRemovableCampaignEmailsArg): Promise<RemovableEmailsPage> => {
    return (await window.PMW.readLocal('emailmarketing/getRemovableCampaignEmails', {
      campaignId: data.campaignId,
      limit: REMOVABLE_EMAILS_FETCH_LIMIT,
      offset: data.offset,
      filter: data.type,
    })) as RemovableEmailsPage;
  }
);

export const fetchCampaignRecipientInsightsTotalRows = createAsyncThunk(
  'fetchCampaignRecipientInsightsTotalRows',
  async (data: FetchCampaignRecipientInsightsTotalRowsArg): Promise<FetchCampaignRecipientInsightsTotalRowsResponse> => {
    return (await window.PMW.readLocal('emailmarketing/getCampaignRecipientInsightsTotalRows', data)) as FetchCampaignRecipientInsightsTotalRowsResponse;
  }
);

export const fetchMailingListRecipientInsights = createAsyncThunk(
  'fetchMailingListRecipientInsights',
  async (data: FetchMailingListRecipientInsightsArg): Promise<FetchMailingListRecipientInsightsResponse> => {
    return (await window.PMW.readLocal('emailmarketing/getMailingListRecipientInsights', data)) as FetchMailingListRecipientInsightsResponse;
  }
);

export const fetchMailingListRecipientInsightsTotalRows = createAsyncThunk(
  'fetchMailingListRecipientInsightsTotalRows',
  async (data: FetchMailingListRecipientInsightsTotalRowsArg): Promise<FetchMailingListRecipientInsightsTotalRowsResponse> => {
    return (await window.PMW.readLocal('emailmarketing/getMailingListRecipientInsightsTotalRows', data)) as FetchMailingListRecipientInsightsTotalRowsResponse;
  }
);

export const fetchRemovableMailingListEmails = createAsyncThunk(
  'fetchRemovableMailingListEmails',
  async (data: FetchRemovableMailingListEmailsArg): Promise<RemovableEmailsPage> => {
    return (await window.PMW.readLocal('emailmarketing/getRemovableMailingListEmails', {
      mailingListId: data.mailingListId,
      limit: REMOVABLE_EMAILS_FETCH_LIMIT,
      offset: data.offset,
      filter: data.type,
    })) as RemovableEmailsPage;
  }
);

export const fetchNegativeOutcomeCounts = createAsyncThunk(
  'fetchNegativeOutcomeCounts',
  async (data: FetchNegativeOutcomeCountsArg): Promise<NegativeOutcomeRecipientCounts> => {
    return (await window.PMW.readLocal('emailmarketing/getNegativeOutcomeCounts', data)) as NegativeOutcomeRecipientCounts;
  }
);

export const removeEmailsForCampaign = createAsyncThunk(
  'removeEmailsForCampaign',
  async (data: RemoveEmailsArg): Promise<void> => {
    await window.PMW.writeLocal('emailmarketing/removeEmailsForCampaign', {id: data.id, type: data.type, emails: JSON.stringify(data.emails)});
  }
);

export const removeAllEmailsOfTypeForCampaign = createAsyncThunk(
  'removeAllEmailsOfTypeForCampaign',
  async (data: RemoveAllEmailsArg): Promise<void> => {
    await window.PMW.writeLocal('emailmarketing/removeEmailsForCampaign', {
      id: data.id,
      type: data.type,
      removeAll: REMOVE_ALL_EMAILS_FLAG,
      excludedEmails: JSON.stringify(data.excludedEmails),
    });
  }
);

export const removeEmailsForMailingList = createAsyncThunk(
  'removeEmailsForMailingList',
  async (data: RemoveEmailsArg): Promise<void> => {
    await window.PMW.writeLocal('emailmarketing/removeEmailsForMailingList', {id: data.id, type: data.type, emails: JSON.stringify(data.emails)});
  }
);

export const removeAllEmailsOfTypeForMailingList = createAsyncThunk(
  'removeAllEmailsOfTypeForMailingList',
  async (data: RemoveAllEmailsArg): Promise<void> => {
    await window.PMW.writeLocal('emailmarketing/removeEmailsForMailingList', {
      id: data.id,
      type: data.type,
      removeAll: REMOVE_ALL_EMAILS_FLAG,
      excludedEmails: JSON.stringify(data.excludedEmails),
    });
  }
);
